import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './forgotPasswordConfirm.module.scss';
import { TextInput, TextInputValue } from '../../components/text-input/TextInput';
import { gql, useMutation } from '@apollo/client';
import { useQueryParams } from '../../hooks/useQueryParams';
import { NavLink, useHistory } from 'react-router-dom';
import { validatePassword } from '../../utils/validation';
import NewAuthPageLayout from '@src/components/auth-page-layout/NewAuthPageLayout';
import Button from '@src/components/button/Button';
import ArrowIcon from '../../assets/icons/curvearrow.svg?react';

type Props = {};

const ResetEmailMutation = gql`
    mutation ResetEmail($password: String!, $token: String!, $region: String!) {
        resetEmailConfirmation(input: { token: $token, password: $password, region: $region })
    }
`

type ResetEmailResponse = {
  resetEmailConfirmation: boolean;
}

function ForgotPasswordConfirm(props: Props) {
  const token = useQueryParams().get('token');
  const region = useQueryParams().get('region');
  const expiry = useQueryParams().get('expiry');
  const history = useHistory();

  useEffect(() => {
    if (Number(expiry) < Date.now()) history.push(`/forgot-password?tokenExpired=${true}`)
  }, [token, history, expiry])

  const [reset, { loading, error }] = useMutation<ResetEmailResponse>(ResetEmailMutation, {
    onCompleted: () => {
      history.push('/login?resetPassword=true')
    },
    onError: () => {
      setCompleted(true);
    }
  });

  const [clientError, setClientError] = useState<undefined | string>();
  const [completed, setCompleted] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChange = useCallback((setter) => (value: TextInputValue) => {
    setClientError(undefined);
    setter(value);
  }, []);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();

    const error = validatePassword(password);
    if (error) {
      return setClientError(error);
    }

    if (password !== confirmPassword) {
      return setClientError('The password and the confirm password should match.')
    }

    reset({ variables: { password, token, region } });
  }, [password, confirmPassword, reset, token, region]);

  const completedContent = useMemo(() => () => {
    if (!completed) return null;
    if (error) {
      return (
        <div className={styles.confirm}>
          <div className={styles.confirmErrorTitle}>Error!</div>
          <div className={styles.confirmSubtitle}>{error.message}</div>
          <div className={styles.footer}>
            Back to <NavLink className={styles.footerLink} to={'/login'}>Login</NavLink>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.confirm}>
        <div className={styles.confirmTitle}>Your password has been set successfully!</div>
        <div/>
        <div className={styles.footer}>
          Back to <NavLink className={styles.footerLink} to={'/login'}>Login</NavLink>
        </div>
      </div>
    )
  }, [completed, error]);

  return (
    <NewAuthPageLayout backToLogin={true} title={<div>Reset your password</div>} buttonTitle={'Reset password'}
                    onSubmit={handleSubmit}
                    loading={loading}>
      {completed ? completedContent :
        <>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'New password'} label={'New password*'}
                     id={'password'}
                     value={password} onChange={handleChange(setPassword)}/>
          <TextInput allowSpecialCharacters type={'password'} className={styles.input} placeholder={'Confirm password'}
                     label={'Confirm password*'}
                     id={'confirmPassword'} value={confirmPassword} onChange={handleChange(setConfirmPassword)}/>
          {!!clientError && <div className={styles.error}>{clientError}</div>}
          <div className={styles.back}>
            <NavLink className={styles.backLink} to={'/login'}>Back to Login</NavLink>
          </div>
          <div className={styles.arrowDiv}>
            <span className={styles.arrowText}>Need help logging in?<br/>Click to Chat with us!</span>
            <ArrowIcon width="40" height="200" />
          </div>
          <Button disabled={loading} className={styles.resetButton} label={'Reset'} type={'submit'} isLoading={loading} />
        </>
      }
    </NewAuthPageLayout>
  );
}

export default ForgotPasswordConfirm;
